import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import NotFound from "@/pages/not-found";
import AuthPage from "@/pages/auth-page";
import { AuthProvider } from "@/hooks/use-auth";
import { ProtectedRoute, AdminRoute } from "./lib/protected-route";
import { DevelopmentIndicator } from "@/components/development-indicator";
import { APP_PATHS, isRouteEnabledInEnvironment } from "@shared/platform/app-metadata";
import { getCurrentAppEnvironment } from "@/lib/current-app-environment";

import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";

const InventoryPage = lazy(() => import("@/pages/inventory-page"));
const AuditPage = lazy(() => import("@/pages/audit-page"));
const UsersPage = lazy(() => import("@/pages/users-page"));
const ContactsPage = lazy(() => import("@/pages/contacts-page"));
const TransactionsPage = lazy(() => import("@/pages/transactions-page"));
const OrdersPage = lazy(() => import("@/pages/orders-page"));
const DashboardPage = lazy(() => import("@/pages/dashboard-page"));
const UpdatePasswordPage = lazy(() => import("@/pages/update-password-page"));

const SuspenseFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="animate-spin h-8 w-8" />
  </div>
);

export function Router() {
  const currentEnvironment = getCurrentAppEnvironment();
  const contactsEnabled = isRouteEnabledInEnvironment("contacts", currentEnvironment);
  const ordersEnabled = isRouteEnabledInEnvironment("orders", currentEnvironment);
  const dashboardEnabled = isRouteEnabledInEnvironment("dashboard", currentEnvironment);

  return (
    <Switch>
      {dashboardEnabled && (
        <ProtectedRoute path={APP_PATHS.dashboard} component={() => (
          <Suspense fallback={<SuspenseFallback />}>
            <DashboardPage />
          </Suspense>
        )} />
      )}
      <ProtectedRoute path={APP_PATHS.inventory} component={() => (
        <Suspense fallback={<SuspenseFallback />}>
          <InventoryPage />
        </Suspense>
      )} />
      <ProtectedRoute path={APP_PATHS.transactions} component={() => (
        <Suspense fallback={<SuspenseFallback />}>
          <TransactionsPage />
        </Suspense>
      )} />
      {ordersEnabled && (
        <ProtectedRoute path={APP_PATHS.orders} component={() => (
          <Suspense fallback={<SuspenseFallback />}>
            <OrdersPage />
          </Suspense>
        )} />
      )}
      {contactsEnabled && (
        <ProtectedRoute path={APP_PATHS.contacts} component={() => (
          <Suspense fallback={<SuspenseFallback />}>
            <ContactsPage />
          </Suspense>
        )} />
      )}
      <ProtectedRoute path={APP_PATHS.audit} component={() => (
        <Suspense fallback={<SuspenseFallback />}>
          <AuditPage />
        </Suspense>
      )} />
      <AdminRoute path={APP_PATHS.users} component={() => (
        <Suspense fallback={<SuspenseFallback />}>
          <UsersPage />
        </Suspense>
      )} />
      <Route path={APP_PATHS.auth} component={AuthPage} />
      <Route path={APP_PATHS.updatePassword} component={() => (
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin h-8 w-8" /></div>}>
            <UpdatePasswordPage />
        </Suspense>
      )} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router />
        <Toaster />
        {process.env.NODE_ENV === 'development' && <DevelopmentIndicator />}
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
